import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Link2, Download, Filter, Search, X, Loader2, ChevronLeft, ChevronRight, ArrowUpDown } from 'lucide-react';
import { BrandLogo } from '../components/layout/BrandLogo';
import { ExampleLinkIcon } from '../components/catalog/ExampleLinkIcon';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/public/catalog`;
const PRICE_KEYS = ['pvp'];
const NUM_KEYS = ['dr', 'traffic'];
const SORTABLE = ['website', 'country', 'pvp', 'dr', 'traffic', 'language'];
const EMPTY = { country: '', category: '', min_price: '', max_price: '', status: '' };
const fmt = (n) => Number(n || 0).toLocaleString('en-US');

const StatCard = ({ value, label, color, testid }) => (
  <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-6 py-5" data-testid={testid}>
    <div className={`text-2xl font-bold ${color}`}>{value}</div>
    <div className="text-sm text-slate-500">{label}</div>
  </div>
);

const Cell = ({ col, value }) => {
  if (col === 'website') {
    return (
      <a href={`https://${String(value).replace(/^https?:\/\//, '')}`} target="_blank" rel="noopener noreferrer"
        className="text-primary hover:text-primary/80 hover:underline font-medium">{value}</a>
    );
  }
  if (PRICE_KEYS.includes(col)) return <span className="font-medium">{value ? `${fmt(value)}€` : '—'}</span>;
  if (NUM_KEYS.includes(col)) return <span>{fmt(value)}</span>;
  if (col === 'category') return <span className="text-slate-500 uppercase text-xs">{value || '—'}</span>;
  return <span className="uppercase text-sm">{value || '—'}</span>;
};

export default function SharedCatalogPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [countries, setCountries] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [applied, setApplied] = useState(EMPTY);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState({ field: 'dr', order: 'desc' });
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const params = useCallback(() => {
    const p = {};
    Object.entries(applied).forEach(([k, v]) => { if (v !== '') p[k] = v; });
    return p;
  }, [applied]);

  useEffect(() => {
    axios.get(`${API}/${token}/countries`).then((r) => setCountries(r.data)).catch(() => {});
  }, [token]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/${token}`, { params: { ...params(), page, limit: 50, sort: sort.field, order: sort.order } })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => {
        if (cancelled) return;
        if (e.response?.status === 401) setNotFound(true);
        else if (e.response?.status === 429) toast.error('Too many requests. Please wait a moment and try again.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token, page, sort, params]);

  const apply = () => { setPage(1); setApplied(form); };
  const reset = () => { setForm(EMPTY); setApplied(EMPTY); setPage(1); };
  const toggleSort = (field) => {
    setPage(1);
    setSort((s) => ({ field, order: s.field === field && s.order === 'desc' ? 'asc' : 'desc' }));
  };

  const download = async () => {
    setDownloading(true);
    try {
      const r = await axios.get(`${API}/${token}/excel`, { params: params(), responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `catalogo_keypubli_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.status === 429 ? 'Download limit reached. Please wait a minute.' : 'Download failed');
    } finally {
      setDownloading(false);
    }
  };

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="shared-catalog-not-found">
        <div className="text-center">
          <Link2 className="h-12 w-12 text-primary mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-800">Catalog not available</h1>
          <p className="text-slate-500 mt-2">This link is invalid or has been disabled.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-primary/5" data-testid="shared-catalog-page">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <BrandLogo className="h-10" />
            <div>
              <h1 className="text-sm font-semibold text-slate-700" data-testid="shared-catalog-title">Product Catalog</h1>
              <p className="text-sm text-slate-500" data-testid="shared-catalog-available">
                {data ? `${fmt(data.total_available)} products available` : 'Loading...'}
              </p>
            </div>
          </div>
          <button onClick={download} disabled={downloading || !data}
            className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 disabled:opacity-60 text-white font-medium text-sm px-5 py-2.5 rounded-lg transition-colors"
            data-testid="shared-catalog-download-btn">
            {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Download Excel
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6 space-y-5">
        <section className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
          <div className="flex items-center gap-2 font-semibold text-slate-800 mb-4"><Filter className="h-4 w-4" /> Filters</div>
          <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_1fr_1fr_auto] gap-3 items-end">
            <div>
              <label className="text-xs text-slate-600 mb-1 block">Country</label>
              <Select value={form.country || 'all'} onValueChange={(v) => setForm({ ...form, country: v === 'all' ? '' : v })}>
                <SelectTrigger data-testid="shared-catalog-country-select"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="all">All countries</SelectItem>
                  {countries.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-slate-600 mb-1 block">Category</label>
              <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                onKeyDown={(e) => e.key === 'Enter' && apply()} placeholder="Search category..."
                className="w-full h-10 border border-slate-200 rounded-md px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                data-testid="shared-catalog-category-input" />
            </div>
            <div>
              <label className="text-xs text-slate-600 mb-1 block">Min price</label>
              <input type="number" min="0" value={form.min_price} onChange={(e) => setForm({ ...form, min_price: e.target.value })}
                onKeyDown={(e) => e.key === 'Enter' && apply()} placeholder="0"
                className="w-full h-10 border border-slate-200 rounded-md px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                data-testid="shared-catalog-min-price-input" />
            </div>
            <div>
              <label className="text-xs text-slate-600 mb-1 block">Max price</label>
              <input type="number" min="0" value={form.max_price} onChange={(e) => setForm({ ...form, max_price: e.target.value })}
                onKeyDown={(e) => e.key === 'Enter' && apply()} placeholder="No limit"
                className="w-full h-10 border border-slate-200 rounded-md px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                data-testid="shared-catalog-max-price-input" />
            </div>
            <div>
              <label className="text-xs text-slate-600 mb-1 block">Show</label>
              <Select value={form.status || 'all'} onValueChange={(v) => setForm({ ...form, status: v === 'all' ? '' : v })}>
                <SelectTrigger data-testid="shared-catalog-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All media</SelectItem>
                  <SelectItem value="new">New (last 30 days)</SelectItem>
                  <SelectItem value="updated">Updated (last 30 days)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <button onClick={apply} className="h-10 inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-medium px-8 rounded-md transition-colors" data-testid="shared-catalog-filter-btn">
                <Search className="h-4 w-4" /> Filter
              </button>
              <button onClick={reset} className="h-10 w-10 inline-flex items-center justify-center border border-slate-200 rounded-md hover:bg-slate-50" data-testid="shared-catalog-reset-btn">
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </section>

        <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard testid="shared-catalog-stat-total" value={fmt(data?.stats.total)} label="Products found" color="text-primary" />
          <StatCard testid="shared-catalog-stat-countries" value={fmt(data?.stats.countries)} label="Countries" color="text-green-600" />
          <StatCard testid="shared-catalog-stat-min" value={`${fmt(data?.stats.min_price)}€`} label="Minimum price" color="text-purple-600" />
          <StatCard testid="shared-catalog-stat-avg" value={`${fmt(data?.stats.avg_price)}€`} label="Average price" color="text-orange-600" />
        </section>

        <section className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden relative">
          {loading && <div className="absolute inset-0 bg-white/60 flex items-center justify-center z-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>}
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="shared-catalog-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  {data?.columns.map((c) => (
                    <th key={c.key} className={`px-3 py-3 text-xs font-medium text-slate-600 whitespace-nowrap ${c.key === 'website' || c.key === 'category' || c.key === 'country' ? 'text-left' : 'text-right'}`}>
                      {SORTABLE.includes(c.key) ? (
                        <button onClick={() => toggleSort(c.key)} className={`inline-flex items-center gap-1 hover:text-primary ${sort.field === c.key ? 'text-primary' : ''}`} data-testid={`shared-catalog-sort-${c.key}`}>
                          {c.label}<ArrowUpDown className="h-3 w-3" />
                        </button>
                      ) : c.label}
                    </th>
                  ))}
                  {data && <th className="px-3 py-3 text-xs font-medium text-slate-600 whitespace-nowrap text-center">Example</th>}
                </tr>
              </thead>
              <tbody>
                {data?.items.map((row, i) => (
                  <tr key={`${row.website}-${i}`} className="border-b border-slate-100 hover:bg-slate-50 transition-colors" data-testid="shared-catalog-row">
                    {data.columns.map((c) => (
                      <td key={c.key} className={`px-3 py-3 ${c.key === 'website' || c.key === 'category' || c.key === 'country' ? 'text-left' : 'text-right'}`}>
                        <Cell col={c.key} value={row[c.key]} />
                        {c.key === 'website' && row.status !== 'unchanged' && (
                          <span className={`ml-2 align-middle text-[10px] font-bold px-1.5 py-0.5 rounded ${row.status === 'new' ? 'bg-primary text-primary-foreground' : 'bg-amber-100 text-amber-700'}`} data-testid={`shared-catalog-badge-${row.status}`}>
                            {row.status === 'new' ? 'NEW' : 'UPDATED'}
                          </span>
                        )}
                      </td>
                    ))}
                    <td className="px-3 py-3 text-center">
                      <ExampleLinkIcon url={row.example_url} testId={`shared-catalog-example-${i}`}
                        label="View publication example" emptyLabel="No publication example" />
                    </td>
                  </tr>
                ))}
                {data && data.items.length === 0 && (
                  <tr><td colSpan={data.columns.length + 1} className="text-center py-12 text-slate-500" data-testid="shared-catalog-empty">No products found</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {data && data.total_pages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 text-sm text-slate-600">
              <span data-testid="shared-catalog-page-info">Page {fmt(page)} of {fmt(data.total_pages)}</span>
              <div className="flex gap-2">
                <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="h-9 w-9 inline-flex items-center justify-center border border-slate-200 rounded-md disabled:opacity-40 hover:bg-slate-50" data-testid="shared-catalog-prev-btn"><ChevronLeft className="h-4 w-4" /></button>
                <button disabled={page >= data.total_pages} onClick={() => setPage(page + 1)} className="h-9 w-9 inline-flex items-center justify-center border border-slate-200 rounded-md disabled:opacity-40 hover:bg-slate-50" data-testid="shared-catalog-next-btn"><ChevronRight className="h-4 w-4" /></button>
              </div>
            </div>
          )}
        </section>
      </main>
      <footer className="max-w-6xl mx-auto px-4 pb-8 text-xs text-slate-500" data-testid="shared-catalog-footer">
        © {new Date().getFullYear()} KeyPubli · Link Building
      </footer>
    </div>
  );
}
